import { SelectionModel } from '@angular/cdk/collections';
import { Platform } from '@angular/cdk/platform';
import { AfterContentInit, Component, ContentChild, ContentChildren, Input, QueryList, TemplateRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { IconName } from '@inscomponents/core';

import { HrefTarget, NavigationRoute } from '@skylab-shared/scenes';
import { CompactViewComponent } from '../compact-view/compact-view.component';
import { Menu } from '../menu/menu.model';
import { OrderingDirection } from '../utils/ordering-direction';
import { GridResizedEvent } from './resize/grid-resizable.directive';
import { GridTemplateDirective } from './template/grid-template.directive';
import { GridTableViewComponent } from './views/grid-table-view/grid-table-view.component';
import { GridTableViewVirtualComponent } from './views/grid-table-virtual/grid-table-view-virtual.component';

export interface SelectionType<TEntity> {
    clear: () => void;
    single: (entity: TEntity) => void;
    multi: (entities: TEntity[]) => void;
    sibling: () => void;
    toggle: (entity: TEntity) => void;
    range: (entity: TEntity) => void;
    allToggle: () => void;
    inverse: () => void;
    selected: () => TEntity[] | undefined | null;
    isSelected: (entity: TEntity) => boolean | undefined | null;
    isAllSelected: () => boolean;
    isIndeterminate: () => boolean;
    isMultipleSelection: () => boolean | null | undefined;
}

export type SortKey<TName> = keyof TName;
export type GridColumnDividerSettings = {
    [param: string]: boolean //param = device width
};
export type ColumnDef<TEntity, TCustomField = never> = keyof TEntity | TCustomField  | (string & {});
export type GridCacheConfig<TColumnIdentity, TSortingIdentity> = {
    columns: (Pick<ServiceGridColumn<TColumnIdentity, TSortingIdentity, unknown>, 'identityName' | 'isHidden'> & { width: string | ColumnWidth })[];
    orderingSettings: {
        identityName: TColumnIdentity,
        direction: OrderingDirection
    },
    columnDividerTouched?: GridColumnDividerSettings;
    summary?: GridSummary,
};
export type GridSorting<TSortingIdentity> = {
    direction: OrderingDirection | null;
    key: TSortingIdentity;
};

export type GridSummary = {
    isVisible: boolean;
};

export type GridVirtualizationConfig = {
    bufferSize?: number;
    maxRows?: number | null;
    headerHeight?: number;
    rowHeight?: number;
    footerHeight?: number;
    scrollBottomOffset?: number;
    scrollToBottomAfterDataChanged?: boolean;
};

export type GridSortingEvent<TSortingIdentity> = GridSorting<TSortingIdentity>;
export interface ServiceGridOptions<TEntity, TColumnIdentity, TSortingIdentity> {
    sorting?: GridSorting<TSortingIdentity>;
    columnDividerTouched?: GridColumnDividerSettings;
    summary?: GridSummary;
    selection: SelectionModel<TEntity> | null;
    stickyHeader?: boolean; // default: true
    hideNoDataRow?: boolean;
    contextMenu?: Menu;
    disabledToToggle?: TColumnIdentity[];
    virtualizationConfig?: GridVirtualizationConfig;
    viewType?: GridViewType;
    isRowItalicStyled?: (entity: TEntity) => boolean,
    gridDefaultOptions?: () => {columns: BehaviorSubject<any>, sorting: GridSorting<TSortingIdentity>, columnDividerTouched: GridColumnDividerSettings, summary?: GridSummary};
    api?: {
        onDisplayedColumnChange?: (columns: Map<TColumnIdentity, boolean>) => void;
        onSortingChange?: (event?: GridSortingEvent<TColumnIdentity>) => void;
        onSummaryChanged?: () => void;
        onColumnResized?: (event: GridResizedEvent<TEntity>, identityName: TColumnIdentity) => void;
        onGridDefaultTriggered?: () => void;
        getInitialEntity?: () => TEntity;
        keyboardControl?: {
            onArrowUp?: (event: KeyboardEvent) => void;
            onArrowDown?: (event: KeyboardEvent) => void;
            onEnter?: (event: KeyboardEvent) => void;
            onSpace?: (event: KeyboardEvent) => void;
            onDelete?: (event: KeyboardEvent) => void;
            onTab?: (event: KeyboardEvent) => void;
        },
        mouseControl?: {
            rowClick?: (event: Event, entity: TEntity) => void;
            rowDblClick?: (event: Event, entity: TEntity) => void;
            rowMiddleClick?: (event: Event, entity: TEntity) => void;
        },
        gridControl?: {
            selection?: SelectionType<TEntity>;
            preventAutoFocus: boolean;
            scrollToBottom?(): void;
        },
        displayedColumns$?: BehaviorSubject<TColumnIdentity[]>
    };
}

export type ColumnWidth = `${number}px`;
type CellClass = 'autosize' | 'button' | 'no-space' | 'show-on-hover' | 'sticky' | 'pointer';
type ColumnClass = 'align-right' | 'align-center';
export type CellClassNames = `cell--${ CellClass }`;
export type ColumnClassNames = `column--${ ColumnClass }`;
export type ActionMethodParameters<TEntity> = { $event: UIEvent, entity: Readonly<TEntity>, index: number };
export type GridTemplateParams<TEntity, TCustomTemplate = never> =
    | {
        template?: 'starTemplate';
        action: (event: PointerEvent, entity: TEntity) => void;
    }
    | {
        template?: 'buttonTemplate';
        action: (event: PointerEvent, entity: TEntity, dataSource?: BehaviorSubject<TEntity[]>) => void;
        isButtonVisible?: (entity: TEntity) => void;
        icon: IconName;
    }
    | {
        template?: 'sklHrefLinkTemplate';
        getRouteObject: (entity: TEntity) => NavigationRoute | null;
        target?: HrefTarget;
    }
    | {
        template?: 'linkActionTemplate';
        action: (entity: TEntity) => void;
    }
    | {
        template?: 'labelTemplate';
    }
    | {
        template?: 'multiSelectTemplate';
    }
    | {
        isVisible?: (entity: TEntity) => boolean;
        template?: 'expandRowButtonTemplate';
    }
    | {
        template?: TCustomTemplate;
    };

type KeyRenamed<T, K extends keyof T, R extends PropertyKey> = Omit<T, K> & (undefined extends T[K] ? { [P in R]?: T[K] } : { [P in R]: T[K] });

export type GridHeaderTemplateParams<TEntity, TCustomTemplate = never> = KeyRenamed<GridTemplateParams<TEntity, TCustomTemplate>, 'template', 'headerTemplate'>;

export type ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, TCustomField = never, TCustomTemplate = never> =
GridTemplateParams<TEntity, TCustomTemplate> &
GridHeaderTemplateParams<TEntity, TCustomTemplate> & {
    identityName: TColumnIdentity;
    columnDef: ColumnDef<TEntity, TCustomField>;
    header?: string;
    headerTooltip?: string;
    cellTooltip?: (entity: TEntity) => string | string[];
    sortingBy?: TSortingIdentity;
    width?: ColumnWidth;
    minWidth?: ColumnWidth;
    label?: string;
    isHidden?: boolean;
    isForcedHidden?: () => boolean;
    isMovable?: boolean;
    isResizable?: boolean;
    summary?: () => string;
    fillingColumn?: boolean;
    cell?: (entity: TEntity) => string;
    styleClass?: CellClassNames | ColumnClassNames | (ColumnClassNames | CellClassNames)[];
};

export type GridViewType = 'table' | 'table-virtual' | 'compact';

@Component({
    selector: 'skl-grid-ins',
    templateUrl: './grid-ins.component.html',
    imports: [GridTableViewComponent, GridTableViewVirtualComponent, CompactViewComponent]
})

export class GridInsComponent<TEntity = unknown, TColumnIdentity = unknown, TSortingIdentity = unknown> implements AfterContentInit {
    @Input('dataSource') public dataSource$!: BehaviorSubject<TEntity[]>;

    @Input('columns') public columns$!: BehaviorSubject<ServiceGridColumn<TColumnIdentity, TSortingIdentity, TEntity, any, any>[]>;

    @Input() public options!: ServiceGridOptions<TEntity, TColumnIdentity, TSortingIdentity>;

    @Input() public displayHeaders = true;

    @ContentChildren(GridTemplateDirective) public customTemplates!: QueryList<GridTemplateDirective<unknown>>;

    @ContentChild('compactViewTemplate', {}) public compactViewTemplate!: TemplateRef<unknown>;

    @ContentChild('noDataRowTemplate', {}) public noDataRowTemplate!: TemplateRef<any>;

    private viewType!: GridViewType;

    public constructor(
        private platformModule: Platform
    ) {}

    public ngAfterContentInit(): void {
        this.initViewType();
    }

    public initViewType(): void {
        const isMobilePlatform = this.platformModule.ANDROID || this.platformModule.IOS;

        this.viewType = isMobilePlatform && this.compactViewTemplate
            ? 'compact'
            : this.options?.viewType || 'table';
    }

    public isCurrentViewType(viewType: GridViewType): boolean {
        return this.viewType === viewType;
    }

    public getRowIndex(entity: TEntity): number {
        return this.dataSource$.value.indexOf(entity);
    }

}
